import generateSettings from './generateSettings';
import buildGoogleGtagSettings from './buildGoogleGtagSettings';
import botFilteringSettings from './botFilteringSettings';
import buildConsentSettings, { resolvePrivacyCookieName } from './buildConsentSettings';
import parseImpliedConsent from './parseImpliedConsent';

/**
 * Build settings template from form data
 *
 * @param {object} params - Parameters
 * @param {array} params.stagedIdentitySyncsData - Staged identity syncs data
 * @param {array} params.stagedIntegrationsData - Staged integrations data
 * @param {object} params.data - Form data
 * @param {string} params.writekey - Pipeline writekey
 * @param {object} params.crossDomainSettings - Cross domain settings
 * @param {boolean} params.optimizeBuild - Optimize build flag
 * @param {array} params.consentCategories - Consent categories
 * @param {boolean} params.queueTasks - Queue tasks flag
 * @param {boolean} params.hasConsentModule - Has consent module flag
 * @returns {object} Settings template
 */
export default function buildSettingsTemplate({
  stagedIdentitySyncsData,
  stagedIntegrationsData,
  data,
  writekey,
  crossDomainSettings,
  optimizeBuild,
  consentCategories,
  queueTasks,
  hasConsentModule,
}) {
  const builderSettings = generateSettings({
    stagedIdentitySyncsData,
    stagedIntegrationsData,
    data,
    consentCategories,
    crossDomainSettings,
  });

  const {
    fileHostingType,
    cdnURL,
    host,
    fileCompression,
    unminifyFile,
    impliedConsent,
    httpCookie,
    windowVariableName,
    storagePrefix,
    storageType,
    storageDatabase,
    botFilterEnabled,
  } = data;

  const { syncInjectorSettings, integrationSettings } = builderSettings;
  const consentBoolean = parseImpliedConsent(impliedConsent);
  const isCompressed = fileCompression === 'true';
  const isUnminified = unminifyFile === 'true';
  const isBotFilteringEnabled = botFilterEnabled === 'true';

  const settingsTemplate = {
    host: host ?? '',
    cdnURL: cdnURL,
    fileHostingType: fileHostingType,
    writeKey: writekey,
    compressFile: isCompressed,
    minifyFile: !isUnminified,
    windowVariableName,
    syncInjectorSettings: {
      queueTasks,
      ...buildGoogleGtagSettings(data),
      ...botFilteringSettings(isBotFilteringEnabled),
      timeout: 10000,
      syncs: syncInjectorSettings,
      privacy: {
        impliedConsent: consentBoolean,
        // A placeholder is harmless only when the emitted consent module actually supplies the policy.
        cookieName: hasConsentModule ? resolvePrivacyCookieName(data) : data.cookieName,
        consentProvider: 'OneTrust',
      },
      storage: {
        type: storageType,
        marker: storagePrefix,
        database: storageDatabase,
      },
      itpSettings: {
        enabled: httpCookie === 'true',
        timeout: 1000,
      },
    },
    integrations: integrationSettings,
    optimizeBuild,
  };

  if (hasConsentModule) {
    settingsTemplate.syncInjectorSettings.consent = buildConsentSettings({
      data,
      consentCategories,
    });
  }

  return settingsTemplate;
}
